import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';

import SectionAccordion from './SectionAccordion';
import UploadFile from '../../../../../components/UploadFile';
import { menuFormTheme, textInputCss } from '../utils/form-styles';
import {
  THEME_COLOR,
  THEME_COLOR3,
  THEME_COLOR4,
  THEME_COLOR5,
  TEXT_SECONDARY_COLOR
} from '../../../../../constants';
import {
  MENU_TYPE_VALUES,
  ALLOWED_ORDER_TYPES,
  MENU_TYPE_LABELS,
  ORDER_TYPE_LABELS,
} from '../schemas/menu-item-schema';
import type { TMenuItemFormValues } from '../schemas/menu-item-schema';
import type { TMenuCategory, TMenuItemImage } from '../types/menu-types';

type TBasicInfoSectionProps = {
  accordionRef: React.RefObject<HTMLElement>;
  categoryList: TMenuCategory[];
  iconColor: string;
  headerBackground: string;
  imageList: TMenuItemImage[];
  imageLoading: boolean;
  onRemoveImage: (data: TMenuItemImage) => void;
  uploadFile: (data: TMenuItemImage, file: File) => void;
  uploadError: (err: string) => void;
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
};

const BasicInfoSection = ({
  accordionRef,
  categoryList,
  iconColor,
  headerBackground,
  imageList,
  imageLoading,
  onRemoveImage,
  uploadFile,
  uploadError,
  expanded,
  onToggle,
}: TBasicInfoSectionProps) => {
  const {
    register,
    control,
    formState: { errors, isSubmitted },
  } = useFormContext<TMenuItemFormValues>();

  const hasError = isSubmitted && !!(errors.name || errors._idCategory);

  return (
    <SectionAccordion
      accordionRef={accordionRef}
      title="Basic info"
      subtitle="Name, description, category, ordering"
      icon={<SellOutlinedIcon fontSize="small" />}
      iconColor={iconColor}
      headerBackground={headerBackground}
      hasError={hasError}
      expanded={expanded}
      onToggle={onToggle}
    >
      <Grid container spacing={2.5}>
        {/* Image upload */}
        <Grid item xs={12}>
          <Typography style={labelStyle}>Item Image</Typography>
          <Box sx={{ width: '100%', aspectRatio: '16/9' }}>
            <UploadFile
              page="addResItemImg"
              uploadText="Drag and Drop your Image Here"
              maxFiles={1}
              imageLength={imageList.length}
              imageLoading={imageLoading}
              imageList={imageList}
              onRemoveImage={onRemoveImage}
              uploadFile={uploadFile}
              uploadError={uploadError}
              uploadHeight="100%"
            />
          </Box>
        </Grid>

        {/* Name */}
        <Grid item xs={12}>
          <ThemeProvider theme={menuFormTheme}>
            <TextField
              required
              focused
              fullWidth
              id="name"
              label="Item Name"
              {...register('name')}
              variant="standard"
              sx={textInputCss}
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
              InputLabelProps={{ style: { color: THEME_COLOR5 }, sx: styles.inputLabel }}
              inputProps={{ autoComplete: 'none', sx: styles.inputText }}
            />
          </ThemeProvider>
          <Typography sx={styles.hint}>This is what customers see on the menu.</Typography>
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <ThemeProvider theme={menuFormTheme}>
            <TextField
              multiline
              focused
              fullWidth
              id="description"
              rows={3}
              label="Description"
              {...register('description')}
              variant="standard"
              sx={textInputCss}
              InputLabelProps={{ style: { color: THEME_COLOR5 }, sx: styles.inputLabel }}
              inputProps={{ autoComplete: 'none', sx: styles.inputText }}
            />
          </ThemeProvider>
        </Grid>

        {/* Category */}
        <Grid item xs={12} md={6}>
          <Typography style={labelStyle}>Category *</Typography>
          <FormControl sx={{ width: '100%' }} error={Boolean(errors._idCategory)}>
            <ThemeProvider theme={menuFormTheme}>
              <Controller
                control={control}
                name="_idCategory"
                render={({ field }) => (
                  <Select
                    variant="standard"
                    sx={{ fontSize: 13, fontFamily: 'GilroyBold', ...textInputCss }}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled sx={styles.menuItem}>
                      <em>Select category...</em>
                    </MenuItem>
                    {categoryList.map((cat) => (
                      <MenuItem key={cat._id} value={cat._id} sx={styles.menuItem}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </ThemeProvider>
            {errors._idCategory && (
              <Typography sx={styles.errorText}>{errors._idCategory.message}</Typography>
            )}
          </FormControl>
        </Grid>

        {/* Menu type */}
        <Grid item xs={12} md={6}>
          <Typography style={labelStyle}>Menu Type</Typography>
          <FormControl sx={{ width: '100%' }}>
            <ThemeProvider theme={menuFormTheme}>
              <Controller
                control={control}
                name="menuType"
                render={({ field }) => (
                  <Select
                    variant="standard"
                    sx={{ fontSize: 13, fontFamily: 'GilroyBold', ...textInputCss }}
                    value={field.value ?? 'none'}
                    onChange={field.onChange}
                  >
                    {MENU_TYPE_VALUES.map((type) => (
                      <MenuItem key={type} value={type} sx={styles.menuItem}>
                        {MENU_TYPE_LABELS[type]}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </ThemeProvider>
          </FormControl>
        </Grid>

        {/* Allowed order types */}
        <Grid item xs={12}>
          <Typography style={labelStyle}>Available for</Typography>
          <Typography sx={styles.hint}>Leave all unchecked to allow all order types.</Typography>
          <Controller
            control={control}
            name="allowedOrderTypes"
            render={({ field }) => {
              const selected = field.value ?? [];
              const toggle = (type: typeof ALLOWED_ORDER_TYPES[number]) => {
                field.onChange(
                  selected.includes(type)
                    ? selected.filter((t) => t !== type)
                    : [...selected, type]
                );
              };
              return (
                <FormGroup row sx={{ mt: 0.5, gap: 0.5 }}>
                  {ALLOWED_ORDER_TYPES.map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          size="small"
                          checked={selected.includes(type)}
                          onChange={() => toggle(type)}
                          sx={{ color: THEME_COLOR4, '&.Mui-checked': { color: THEME_COLOR } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 12, color: '#000' }}>
                          {ORDER_TYPE_LABELS[type]}
                        </Typography>
                      }
                    />
                  ))}
                </FormGroup>
              );
            }}
          />
        </Grid>
      </Grid>
    </SectionAccordion>
  );
};

export default BasicInfoSection;

const labelStyle = { color: THEME_COLOR5, fontFamily: 'GilroyRegular', fontSize: 12 } as React.CSSProperties;

const styles = {
  inputLabel: { fontFamily: 'GilroyRegular' },
  inputText: { fontSize: 13, fontFamily: 'GilroySemiBold', my: 0.8 },
  menuItem: { fontSize: 13, fontFamily: 'GilroyBold' },
  hint: { mt: 0.5, fontFamily: 'GilroyRegular', fontSize: 12, color: TEXT_SECONDARY_COLOR },
  errorText: { fontFamily: 'GilroyRegular', fontSize: 11, color: '#d32f2f', mt: 0.5 },
};
